import { Injectable, NgZone, OnDestroy } from '@angular/core'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import { TerraDraw, TerraDrawPolygonMode } from 'terra-draw'
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter'

export interface PolygonDrawStyle {
  fillColor?: string
  outlineColor?: string
}

const DEFAULT_STYLE: Required<PolygonDrawStyle> = {
  fillColor: '#f74a55',
  outlineColor: '#f74a55'
}

/**
 * Replacement for `google.maps.drawing.DrawingManager`, which Google deprecated in
 * August 2025 and removed from the Maps JS API in v3.65.3b. Wraps Terra Draw (the
 * migration path Google itself points at) behind the enable/disable/complete shape
 * the map components already used.
 *
 * Terra Draw only produces the polygon; as with the old DrawingManager the finished
 * shape is discarded here and the component builds its own editable
 * `google.maps.Polygon` from the emitted points. Provide per-component, not in root —
 * one instance belongs to one map.
 */
@Injectable()
export class PolygonDrawService implements OnDestroy {
  private draw: TerraDraw = null
  private started = false
  private idleListener: google.maps.MapsEventListener = null
  private readonly _polygonComplete = new Subject<google.maps.LatLngLiteral[]>()
  private readonly _initialized = new BehaviorSubject<boolean>(false)
  private readonly _allowed = new BehaviorSubject<boolean>(false)
  private readonly _active = new BehaviorSubject<boolean>(false)

  /** Emits the vertices of a freshly drawn polygon, open (no duplicated closing point). */
  readonly polygonComplete$: Observable<google.maps.LatLngLiteral[]> = this._polygonComplete.asObservable()
  readonly initialized$: Observable<boolean> = this._initialized.asObservable()
  /** True while drawing is permitted at all — bind the draw button's visibility to this. */
  readonly allowed$: Observable<boolean> = this._allowed.asObservable()
  /**
   * True while the polygon tool is armed. The old DrawingManager toolbar let the user
   * drop back to a pan cursor without drawing; `toggle()` plus this flag keep that escape
   * hatch, since Terra Draw ships no toolbar of its own.
   */
  readonly active$: Observable<boolean> = this._active.asObservable()

  constructor(private ngZone: NgZone) {}

  get initialized(): boolean {
    return this._initialized.value
  }

  init(map: google.maps.Map, style: PolygonDrawStyle = {}): void {
    if (this.draw) return

    const { fillColor, outlineColor } = { ...DEFAULT_STYLE, ...style }

    this.draw = new TerraDraw({
      adapter: new TerraDrawGoogleMapsAdapter({ lib: google.maps, map }),
      modes: [
        new TerraDrawPolygonMode({
          // Vertex editing is done on the google.maps.Polygon the component builds,
          // so Terra Draw only has to handle the initial draw.
          editable: false,
          styles: {
            fillColor: fillColor as any,
            outlineColor: outlineColor as any,
            fillOpacity: 0.3,
            outlineWidth: 2,
            closingPointColor: outlineColor as any,
            closingPointOutlineColor: '#ffffff'
          }
        })
      ]
    })

    this.draw.on('finish', (id, context) => {
      if (context.action !== 'draw') return
      const feature = this.draw.getSnapshotFeature(id)
      // The finished shape is handed to the component as plain points; it renders its
      // own editable google.maps.Polygon, exactly as it did from 'overlaycomplete'.
      this.draw.clear()
      this.disable()
      if (!feature || feature.geometry.type !== 'Polygon') return
      const points = ringToLatLngLiterals(feature.geometry.coordinates[0] as number[][])
      // Terra Draw dispatches from a native DOM handler; re-enter Angular so the
      // component's downstream state updates trigger change detection.
      this.ngZone.run(() => this._polygonComplete.next(points))
    })

    // Terra Draw's adapter binds its pointer handlers to Google's interactive overlay
    // (`div[style*="z-index: 3;"]`), which Google only creates once the map has actually
    // rendered — starting at agm's (mapReady) throws on a null element. A map inside an
    // inactive tab never renders at all, so listen until it settles rather than once.
    if (map.getBounds()) {
      this.startDraw()
    } else {
      this.idleListener = map.addListener('idle', () => this.ngZone.run(() => this.startDraw()))
    }

    this._initialized.next(true)
  }

  private startDraw(): void {
    if (!this.draw || this.started) return
    this.draw.start()
    this.started = true
    this.clearIdleListener()
    this.applyMode()
  }

  /**
   * Mode changes requested before the map has rendered are held in _active and applied
   * on start, so callers can enable/disable straight after init() as they always have.
   */
  private applyMode(): void {
    if (!this.draw || !this.started) return
    this.draw.setMode(this._active.value ? 'polygon' : 'static')
  }

  private clearIdleListener(): void {
    if (this.idleListener) {
      this.idleListener.remove()
      this.idleListener = null
    }
  }

  /** Permit drawing and arm the polygon tool. */
  enable(): void {
    if (!this.draw) return
    this._allowed.next(true)
    this._active.next(true)
    this.applyMode()
  }

  /** Forbid drawing entirely — the draw button hides. */
  disable(): void {
    if (!this.draw) return
    this._allowed.next(false)
    this._active.next(false)
    this.applyMode()
  }

  /** Arm or disarm the polygon tool without withdrawing permission to draw. */
  toggle(): void {
    if (!this.draw || !this._allowed.value) return
    this._active.next(!this._active.value)
    this.applyMode()
  }

  ngOnDestroy(): void {
    this.clearIdleListener()
    if (this.draw) {
      if (this.draw.enabled) {
        this.draw.stop()
      }
      this.draw = null
      this.started = false
    }
    this._polygonComplete.complete()
    this._initialized.complete()
    this._allowed.complete()
    this._active.complete()
  }
}

/**
 * GeoJSON rings are closed (last position repeats the first) and ordered lng/lat.
 * The old DrawingManager handed back an open path, and the coordinate tables
 * downstream still assume that, so drop the repeated position.
 */
function ringToLatLngLiterals(ring: number[][]): google.maps.LatLngLiteral[] {
  const positions = ring.slice()
  if (positions.length > 1) {
    const first = positions[0]
    const last = positions[positions.length - 1]
    if (first[0] === last[0] && first[1] === last[1]) {
      positions.pop()
    }
  }
  return positions.map(([lng, lat]) => ({ lat, lng }))
}
